import {
    Card,
    CardMedia,
    Dialog,
    DialogContent,
    IconButton,
    useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useState } from 'react';

export interface ContentScreenshotThumbnailProps {
    /** Pre-resolved screenshot URL (e.g. from theme-aware getScreenshotUrl). Takes precedence. */
    screenshotUrl?: string | null;
    /** URL for light theme screenshot */
    screenshotLight?: string | null;
    /** URL for dark theme screenshot */
    screenshotDark?: string | null;
    /** Alt text for the image */
    alt?: string;
    /** Thumbnail width (default 160) */
    width?: number;
    /** Thumbnail height (default 100) */
    height?: number;
    /** Optional data-attr for testing */
    'data-attr'?: string;
}

/**
 * Reusable thumbnail for coach content and lesson plan screenshots.
 * Renders a thumbnail; click opens a full-screen popup to view the image.
 */
const ContentScreenshotThumbnail: React.FC<ContentScreenshotThumbnailProps> = ({
    screenshotUrl: screenshotUrlProp,
    screenshotLight,
    screenshotDark,
    alt = 'Preview',
    width = 160,
    height = 100,
    'data-attr': dataAttr
}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);

    // Use pre-resolved URL, or pick based on theme from light/dark
    const screenshotUrl = screenshotUrlProp ?? (() => {
        const isDark = theme.palette.mode === 'dark';
        if (isDark && screenshotDark) return screenshotDark;
        if (screenshotLight) return screenshotLight;
        if (screenshotDark) return screenshotDark;
        return null;
    })();

    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);

    if (!screenshotUrl) return null;

    return (
        <>
            <Card
                onClick={handleOpen}
                sx={{
                    width,
                    height,
                    flexShrink: 0,
                    borderRadius: 1,
                    overflow: 'hidden',
                    boxShadow: 1,
                    cursor: 'pointer',
                    '&:hover': {
                        boxShadow: 2,
                        opacity: 0.95
                    },
                    transition: 'box-shadow 0.2s, opacity 0.2s'
                }}
                data-attr={dataAttr}
            >
                <CardMedia
                    component="img"
                    image={screenshotUrl}
                    alt={alt}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top center'
                    }}
                />
            </Card>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        bgcolor: 'background.default'
                    }
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative'
                    }}
                    onClick={handleClose}
                >
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleClose();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'grey.300',
                            zIndex: 1,
                            '&:hover': { color: 'white' }
                        }}
                        aria-label="Close"
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                    <img
                        src={screenshotUrl}
                        alt={alt}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '95vh',
                            objectFit: 'contain'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ContentScreenshotThumbnail;
